import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User, AuthTokens, UpdateUserProfileRequest, WebSocketEvent } from '@sahaj/types';
import { decodeJwt, isTokenExpired } from '@sahaj/auth';
import { api, tokenStorage, ws } from '../lib/api';
import { topics } from '@sahaj/websocket';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (tokens: AuthTokens) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateProfile: (data: UpdateUserProfileRequest) => Promise<User>;
  uploadAvatar: (file: File | Blob) => Promise<User>;
  removeAvatar: () => Promise<User>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    const token = await tokenStorage.getAccessToken();
    if (!token || isTokenExpired(token)) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const decoded = decodeJwt(token);
      if (decoded?.sub) {
        const currentUser = await api.auth.me();
        setUser(currentUser);
      }
    } catch (err) {
      console.error('Failed to load user profile', err);
      await tokenStorage.clearTokens();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Subscribe to real-time user updates
  useEffect(() => {
    if (!user) return;

    const unsub = ws.subscribe(topics.notifications(), (event: WebSocketEvent) => {
      if (event.eventType === 'USER_UPDATED' && event.payload) {
        const updatedUser = event.payload as User;
        if (updatedUser.id === user.id) {
          setUser(updatedUser);
        }
      }
    });

    return () => {
      unsub();
    };
  }, [user?.id]);

  const login = async (tokens: AuthTokens) => {
    await api.setTokens(tokens);
    await fetchCurrentUser();
  };

  const logout = () => {
    tokenStorage.getRefreshToken().then((refreshToken) => {
      if (refreshToken) {
        api.auth.logout({ refreshToken }).catch(() => {});
      }
      api.clearTokens().then(() => setUser(null));
    });
  };

  const updateProfile = async (data: UpdateUserProfileRequest) => {
    const updated = await api.users.updateMe(data);
    setUser(updated);
    return updated;
  };

  const uploadAvatar = async (file: File | Blob) => {
    const updated = await api.users.uploadAvatar(file);
    setUser(updated);
    return updated;
  };

  const removeAvatar = async () => {
    const updated = await api.users.deleteAvatar();
    setUser(updated);
    return updated;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser: fetchCurrentUser,
        updateProfile,
        uploadAvatar,
        removeAvatar,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
